import React from 'react';
import { useTranslation } from 'react-i18next';

const SCHEDULE_TEMPLATES = {
  breakfast: { name: 'Breakfast', dayOfWeek: 'all', startTime: '07:00', endTime: '11:00' },
  lunch: { name: 'Lunch', dayOfWeek: 'all', startTime: '11:00', endTime: '15:00' },
  dinner: { name: 'Dinner', dayOfWeek: 'all', startTime: '17:00', endTime: '22:00' },
  happy_hour: { name: 'Happy Hour', dayOfWeek: 'all', startTime: '17:00', endTime: '19:00' },
};

function formatScheduleTime(timeStr) {
  if (!timeStr) return '';
  const s = String(timeStr).slice(0, 5);
  const [h, m] = s.split(':').map(Number);
  if (h === 12) return `12:${String(m).padStart(2, '0')} PM`;
  if (h === 0) return `12:${String(m).padStart(2, '0')} AM`;
  return h > 12 ? `${h - 12}:${String(m).padStart(2, '0')} PM` : `${h}:${String(m).padStart(2, '0')} AM`;
}

export default function ScheduleModal({
  item,
  schedules,
  loading,
  newSchedule,
  saving,
  onClose,
  onNewScheduleChange,
  onAddSchedule,
  onDeleteSchedule,
}) {
  const { t } = useTranslation();

  const applyTemplate = (key) => {
    const tpl = SCHEDULE_TEMPLATES[key];
    if (tpl) onNewScheduleChange(tpl);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content schedule-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('admin.schedule.title', 'Availability Schedule')} – {item.name}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <h3>{t('admin.schedule.currentSchedules', 'Current Schedules')}</h3>
          {loading ? (
            <p className="text-muted">{t('common.loading')}…</p>
          ) : schedules.length === 0 ? (
            <div className="empty-state">
              <p>{t('admin.schedule.noSchedules', 'No schedules set. This item is available all day.')}</p>
            </div>
          ) : (
            <div className="schedules-list">
              {schedules.map((schedule) => (
                <div key={schedule.id} className="schedule-item">
                  <div className="schedule-info">
                    <strong>{schedule.schedule_name || t('admin.schedule.custom', 'Custom Schedule')}</strong>
                    <div className="schedule-details">
                      <span className="schedule-day">
                        {schedule.day_of_week === 'all' ? t('admin.schedule.everyDay', 'Every day') : schedule.day_of_week}
                      </span>
                      <span className="schedule-time">
                        {formatScheduleTime(schedule.start_time)} – {formatScheduleTime(schedule.end_time)}
                      </span>
                    </div>
                  </div>
                  <div className="schedule-actions">
                    <button type="button" className="btn btn-icon btn-danger" onClick={() => onDeleteSchedule(schedule.id)} title={t('common.delete')}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <hr />
          <h3>{t('admin.schedule.quickTemplates', 'Quick Templates')}</h3>
          <div className="template-buttons">
            <button type="button" className="btn btn-secondary btn-small" onClick={() => applyTemplate('breakfast')}>🌅 Breakfast (7am–11am)</button>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => applyTemplate('lunch')}>🍽️ Lunch (11am–3pm)</button>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => applyTemplate('dinner')}>🌙 Dinner (5pm–10pm)</button>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => applyTemplate('happy_hour')}>🍹 Happy Hour (5pm–7pm)</button>
          </div>
          <h3>{t('admin.schedule.addNew', 'Add New Schedule')}</h3>
          <div className="schedule-form">
            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.schedule.scheduleName', 'Schedule Name')} ({t('common.optional', 'optional')})</label>
                <input
                  type="text"
                  className="form-input"
                  value={newSchedule.name}
                  onChange={(e) => onNewScheduleChange({ ...newSchedule, name: e.target.value })}
                  placeholder="e.g. Breakfast, Weekend Brunch"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.schedule.dayOfWeek', 'Day of Week')}</label>
                <select
                  className="form-input"
                  value={newSchedule.dayOfWeek}
                  onChange={(e) => onNewScheduleChange({ ...newSchedule, dayOfWeek: e.target.value })}
                >
                  <option value="all">{t('admin.schedule.everyDay', 'Every Day')}</option>
                  <option value="monday">Monday</option>
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday</option>
                  <option value="thursday">Thursday</option>
                  <option value="friday">Friday</option>
                  <option value="saturday">Saturday</option>
                  <option value="sunday">Sunday</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.schedule.startTime', 'Start Time')}</label>
                <input
                  type="time"
                  className="form-input"
                  value={newSchedule.startTime}
                  onChange={(e) => onNewScheduleChange({ ...newSchedule, startTime: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{t('admin.schedule.endTime', 'End Time')}</label>
                <input
                  type="time"
                  className="form-input"
                  value={newSchedule.endTime}
                  onChange={(e) => onNewScheduleChange({ ...newSchedule, endTime: e.target.value })}
                />
              </div>
            </div>
            <button type="button" className="btn btn-primary" onClick={onAddSchedule} disabled={saving}>
              {saving ? '…' : t('admin.schedule.addButton', 'Add Schedule')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
